import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RouteComponentProps } from 'react-router'
import { api } from 'core/api/ConnectionManager'
import deepEqual from 'fast-deep-equal'
import debounce from 'lodash/debounce'
import { getURLSearchParamsByLocation } from 'features/routes/utils'
import helpers from 'core/helpers'
import { MapMarker, TMapPoint } from 'features/map/models/map'
import { MAP_CENTER, MAP_DEVICES_DISTANCE_KM } from 'features/map/constants'

const API_CALL_DELAY = 1000

export interface ICatalogDisplaysMapItem extends MapMarker {}

interface ICatalogDisplaysMapOption {
    id: string
}

interface ICatalogDisplaysMapTag {
    id: string | number
    name: string
}

type DisplaysFilter = Record<string, string[]>

export interface ICatalogDisplaysMapOwnProps {
    onSelectCluster: (items: ICatalogDisplaysMapItem[]) => void
    onSelectInfoByItem: (item: ICatalogDisplaysMapItem) => void
    centerMap: (center: TMapPoint) => void
    center?: TMapPoint | null
    list: ICatalogDisplaysMapItem[]
    zoom?: number
    selectedItems: ICatalogDisplaysMapItem[]
    selectedInfo?: ICatalogDisplaysMapItem | null
    markerColor?: string
    activeMarkerColor?: string
    options?: ICatalogDisplaysMapOption[] | null
    tags?: ICatalogDisplaysMapTag[]
    onGetList: (items: ICatalogDisplaysMapItem[]) => void
}

export type ICatalogDisplaysMapProps = ICatalogDisplaysMapOwnProps & RouteComponentProps

const getFilter = (p_: ICatalogDisplaysMapProps): DisplaysFilter => {
    const query = helpers.getQuery(getURLSearchParamsByLocation(p_.location), 'displays')

    return (p_.options ?? []).reduce<DisplaysFilter>((filter, field) => {
        const queryValue = query[field.id]

        if (!queryValue) {
            return filter
        }

        const values = queryValue.split('//')
        filter[field.id] =
            field.id === 'tags'
                ? values.map((value) => {
                      const tag = p_.tags?.find(({ name }) => name === value)
                      return tag ? String(tag.id) : value
                  })
                : values

        return filter
    }, {})
}

const CatalogDisplaysMapMethods = (p_: ICatalogDisplaysMapProps, userLocation: TMapPoint | null) => {
    const [distance, setDistance] = useState(MAP_DEVICES_DISTANCE_KM)
    const previousFilter = useRef(getFilter(p_))
    const currentProps = useRef(p_)
    const initialData = useRef({
        center: p_.center,
        distance: MAP_DEVICES_DISTANCE_KM,
        location: p_.location,
        onGetList: p_.onGetList,
        userLocation,
    })
    currentProps.current = p_

    const getDisplaysByDistanceDebounce = useMemo(
        () =>
            debounce((location: TMapPoint, currentDistance: number, type?: 'update', filter?: DisplaysFilter) => {
                api.send<object, ICatalogDisplaysMapItem[]>('getDisplays', {
                    ...filter,
                    ...location,
                    distance: currentDistance,
                }).then((newList) => {
                    const props = currentProps.current
                    const normalizedList = (newList || [])
                        .map((item) => ({
                            ...item,
                            lat: Number(item.lat),
                            lng: Number(item.lng),
                        }))
                        .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng))

                    const list =
                        type === 'update'
                            ? [
                                  ...props.list,
                                  ...normalizedList.filter(
                                      (item) => !props.list.some((currentItem) => currentItem.id === item.id)
                                  ),
                              ]
                            : normalizedList

                    props.onGetList(list)
                })
            }, API_CALL_DELAY),
        []
    )

    const getDisplaysByDistance = useCallback(
        (location: TMapPoint, currentDistance: number, type?: 'update', filter?: DisplaysFilter) => {
            getDisplaysByDistanceDebounce(location, currentDistance, type, filter)
        },
        [getDisplaysByDistanceDebounce]
    )

    useEffect(() => {
        const initial = initialData.current
        const query = helpers.getQuery(getURLSearchParamsByLocation(initial.location), 'displays')
        const initialCenter = initial.center || initial.userLocation || MAP_CENTER

        if (!query.query) {
            getDisplaysByDistance(initialCenter, initial.distance)
        } else {
            initial.onGetList([])
        }
    }, [getDisplaysByDistance])

    useEffect(() => {
        const filter = getFilter(p_)

        if (!deepEqual(previousFilter.current, filter) && p_.center) {
            getDisplaysByDistance(p_.center, distance, 'update', filter)
        }

        previousFilter.current = filter
    }, [p_, distance, getDisplaysByDistance])

    useEffect(() => () => getDisplaysByDistanceDebounce.cancel(), [getDisplaysByDistanceDebounce])

    const onChange = (center: TMapPoint, currentDistance: number) => {
        setDistance(currentDistance)
        getDisplaysByDistance(center, currentDistance, 'update', getFilter(p_))
    }

    return {
        onChange,
    }
}

export default CatalogDisplaysMapMethods
